import React, { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider, useSocket } from './contexts/SocketContext';
import CanvasGame from './components/CanvasGame';
import BettingControls from './components/BettingControls';
import LiveBets from './components/LiveBets';
import TopRidersStrip from './components/TopRidersStrip';
import ProvablyFair from './components/ProvablyFair';
import GameHistory from './components/GameHistory';
import Auth from './components/Auth';
import ConnectionBanner from './components/ConnectionBanner';
import AudioControls from './components/AudioControls';
import { playInterfaceClick } from './components/interfaceAudio';
import GameRules from './components/GameRules';
import AccountSettings from './components/AccountSettings';
import MyStats from './components/MyStats';
import AdminDashboard from './admin/AdminDashboard';
import PresentationSettings from './components/PresentationSettings';
import { RiderSheetHost } from './components/RiderActions';
import ErrorBoundary from './components/ErrorBoundary';
import { DISPLAY_CONFIG } from './config/display';
import { loadPresentationSettings, savePresentationSettings, type PresentationSettings as PresentationSettingsState } from './config/presentationSettings';
import { formatMoney } from './utils/money';
import { isNativePlatform, markNativeShell } from './utils/platform';
import { pinNativeTopLayer } from './utils/visualViewportPin';
import whiteoutLogo from './assets/whiteout-logo.png';

// Before the first render: index.css keys the app-only touch rules off html.native.
markNativeShell();

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="section-label">{children}</p>
);

type Overlay = null | 'stats' | 'account' | 'presentation';

export const Header: React.FC<{ onOpen: (panel: Overlay) => void }> = ({ onOpen }) => {
  const { user, logout, logoutError } = useAuth();
  const { player, gameInfo, isConnected, devSimulation } = useSocket();
  const displayName = user?.username ?? player?.username ?? null;
  const phase = gameInfo?.phase;
  const phaseLabel = devSimulation.active ? `Local sim · ${phase?.replace('_', ' ').toLowerCase() ?? 'isolated'}` :
    phase === 'STARTING' ? 'Bets open' :
    phase === 'LAUNCHING' ? 'Dropping in' :
    phase === 'IN_PROGRESS' ? 'Round live' :
    phase === 'CRASHED' ? 'Whiteout' : 'Connecting';

  return (
    <header className="app-header animate-in">
      <div className="brand-lockup" aria-label={`${DISPLAY_CONFIG.brandName}: ${DISPLAY_CONFIG.tagline}`}>
        <img className="brand-mark" src={whiteoutLogo} alt="" aria-hidden="true" />
        <div>
          <h1>{DISPLAY_CONFIG.brandName}</h1>
          <p>{DISPLAY_CONFIG.tagline}</p>
        </div>
      </div>

      <div className="header-status" aria-live="polite">
        <span className={`connection-dot${isConnected || devSimulation.active ? ' is-online' : ''}`} aria-hidden="true" />
        <span>{phaseLabel}</span>
      </div>

      <div className="header-actions">
        {/* Shown only to administrators on the web; the store app has no admin tools.
            The server still checks the role on every admin request. */}
        {user?.role === 'admin' && !isNativePlatform() && <a className="admin-entry" href="/admin">Admin</a>}
        {player && <div className="balance-chip"><span>Balance</span><strong>{formatMoney(player.balance)}</strong></div>}
        <AudioControls />
        <div className="account-menu">
          <div className="account-avatar">{displayName ? displayName.slice(0, 2).toUpperCase() : '—'}</div>
          <div className="account-copy">
            <span>Signed in</span>
            <strong>{displayName ?? 'Player'}</strong>
          </div>
          {logoutError && <span role="alert">{logoutError}</span>}
          <button onClick={() => onOpen('stats')} className="icon-button" aria-label="Your statistics" title="Your statistics">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
            </svg>
          </button>
          <button onClick={() => onOpen('account')} className="icon-button" aria-label="Account settings" title="Account settings">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" />
            </svg>
          </button>
          <button onClick={logout} className="icon-button signout-button" aria-label="Sign out" title="Sign out">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 17l5-5-5-5M15 12H3M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};

function MobileInsights({ showCurrentConditions }: { showCurrentConditions: boolean }) {
  const [tab, setTab] = useState<'bets' | 'fairness'>('bets');
  return (
    <section className="mobile-insights panel">
      <div className="mobile-tabs" role="tablist" aria-label="Round insights">
        <button role="tab" aria-selected={tab === 'bets'} onClick={() => setTab('bets')}>Live bets</button>
        <button role="tab" aria-selected={tab === 'fairness'} onClick={() => setTab('fairness')}>Fairness</button>
      </div>
      <div className="mobile-tab-content">{tab === 'bets' ? <LiveBets showCurrentConditions={showCurrentConditions} /> : <div className="mobile-trust-stack"><ProvablyFair /><GameRules /></div>}</div>
    </section>
  );
}

/** A settings or stats panel over the game, dismissable with Escape. */
const OverlayPanel: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="overlay-backdrop" role="dialog" aria-modal="true" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="overlay-body">{children}</div>
    </div>
  );
};

function GameCanvas(props: React.ComponentProps<typeof CanvasGame>) {
  const { friends } = useSocket();
  return <CanvasGame key={friends?.code || 'ungrouped'} {...props} soloMode={friends?.code ? false : props.soloMode} />;
}

function GameScreen() {
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [soloMode, setSoloMode] = useState(() => localStorage.getItem('slopeSoloMode') === 'true');
  const [presentation, setPresentation] = useState(loadPresentationSettings);
  const updatePresentation = (key: keyof PresentationSettingsState, value: boolean) => {
    setPresentation(current => {
      const next = { ...current, [key]: value };
      savePresentationSettings(next);
      return next;
    });
  };
  const handleSoloModeChange = (solo: boolean) => {
    setSoloMode(solo);
    localStorage.setItem('slopeSoloMode', String(solo));
  };

  return (
    <SocketProvider>
      <div className="app-shell" onClickCapture={playInterfaceClick}>
      <ConnectionBanner />
        <Header onOpen={setOverlay} />
        {overlay && (
          <OverlayPanel onClose={() => setOverlay(null)}>
            {overlay === 'account' ? <AccountSettings onClose={() => setOverlay(null)} />
              : overlay === 'presentation' ? <PresentationSettings settings={presentation} onChange={updatePresentation} onClose={() => setOverlay(null)} />
              : <MyStats onClose={() => setOverlay(null)} />}
          </OverlayPanel>
        )}
        <section className="history-bar" aria-label="Recent round history">
          <GameHistory />
        </section>

        <main className="game-layout">
          <aside className="live-rail panel animate-in delay-1">
            <div className="panel-heading"><SectionLabel>Live Players</SectionLabel><span className="live-badge">Live</span></div>
            <LiveBets showCurrentConditions={presentation.showCurrentConditions} />
          </aside>

          <section className="game-stage-wrap animate-in delay-1">
            <div className="stage-heading">
              <div>
                <SectionLabel>{DISPLAY_CONFIG.brandName} run</SectionLabel>
                <h2>Cash out before the mountain turns.</h2>
              </div>
              <div className="stage-actions">
                <span className="demo-badge">Free play</span>
                <button onClick={() => setOverlay('presentation')} className="display-settings-trigger" aria-label="Presentation settings" title="Presentation settings">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" /><circle cx="16" cy="6" r="2" /><circle cx="8" cy="12" r="2" /><circle cx="13" cy="18" r="2" />
                  </svg>
                  <span>Display</span>
                </button>
              </div>
            </div>
            <div className="game-stage panel"><GameCanvas soloMode={soloMode} showSlopeTrace={presentation.showSlopeTrace} showRunLevelGraph={presentation.showRunLevelGraph} showRunLevelTitles={presentation.showRunLevelTitles} /></div>
            <TopRidersStrip />
            <div className="stage-footnote">
              <span>{DISPLAY_CONFIG.freePlayLabel}</span>
              <span>18+ · Play credits only · No real money or prizes</span>
            </div>
          </section>

          <aside className="control-rail animate-in delay-2">
            <section className="panel bet-panel">
              <div className="panel-heading"><SectionLabel>Place wager</SectionLabel><span className="single-bet-label">Single bet</span></div>
              <BettingControls soloMode={soloMode} onSoloModeChange={handleSoloModeChange} />
            </section>
            <section className="panel fairness-panel"><ProvablyFair /></section>
            <section className="panel rules-panel"><GameRules /></section>
          </aside>

          <MobileInsights showCurrentConditions={presentation.showCurrentConditions} />
        </main>
        <RiderSheetHost />
      </div>
    </SocketProvider>
  );
}

const isAdminPath = (pathname: string) => /^\/admin(\/|$)/.test(pathname);

function AppContent() {
  const { isAuthenticated, isLoading, sessionUnverified, refreshUser } = useAuth();
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-lockup">
          <img src={whiteoutLogo} alt="" aria-hidden="true" />
          <div className="loading-logo">{DISPLAY_CONFIG.brandName}</div>
        </div>
        <div className="loading-track"><span /></div>
        {sessionUnverified
          ? <p role="status">Can't reach the server. Retrying… <button type="button" className="loading-retry" onClick={() => { void refreshUser(); }}>Retry now</button></p>
          : <p>Preparing the mountain</p>}
      </div>
    );
  }
  // No router (spec 005 research R-2): /admin is a full-page switch that also
  // tears down the game canvas and socket while administering. Web only.
  if (!isNativePlatform() && isAdminPath(window.location.pathname)) return <AdminDashboard />;
  return isAuthenticated ? <GameScreen /> : <Auth />;
}

export default function App() {
  return <>
    {/* In the app the scrim follows the visual viewport, so it stays under the
        clock while the keyboard pans the page (utils/visualViewportPin). */}
    <div className="status-bar-scrim" aria-hidden="true" ref={pinNativeTopLayer} />
    <ErrorBoundary><AuthProvider><AppContent /></AuthProvider></ErrorBoundary>
  </>;
}
